import React, {
  useContext,
  useLayoutEffect,
  useEffect,
  useState,
  useRef,
} from 'react';
import InView from 'react-intersection-observer';
import anime from 'animejs';

import { map, constrain } from '@helpers/mathUtils';
import { ViewContext } from '@helpers/contexts';

const ScrollMotion = ({
  enableEnterAnim,
  disabled,
  rotate,
  translate,
  children,
  ...other
}) => {
  const { scroll, mobileView, windowHeight } = useContext(ViewContext);
  const [inView, setInView] = useState(false);
  const [hasInit, setInit] = useState(true);
  const [elemOffsetTop, setElemOffsetTop] = useState(-1);
  const wrapperEl = useRef();

  const initAnim = () => {
    if (wrapperEl) {
      wrapperEl.current.style.transition = null;
      anime({
        targets: wrapperEl.current,
        duration: 1000,
        translateY: ['100%', '0%'],
        scale: [1.5, 1],
        rotate: rotate.start,
        easing: 'easeInOutQuad',
        complete: () => {
          wrapperEl.current.style.transition = '0.4s ease-out';
          setInit(true);
        },
      });
    }
  };

  const animate = () => {
    if (wrapperEl) {
      let nRot = 0,
        nTrans = 0;

      if (rotate) {
        const rot = map(
          scroll - elemOffsetTop,
          0,
          windowHeight,
          rotate.start,
          rotate.end
        );
        nRot = constrain(rot, rotate.start, rotate.end);
      }

      if (translate) {
        const trans = map(
          scroll - elemOffsetTop,
          0,
          windowHeight,
          translate.start,
          translate.end
        );
        nTrans = constrain(trans, translate.start, translate.end);
      }

      wrapperEl.current.style.transform = `rotate(${nRot}deg) translate3d(0, ${nTrans}px, 0)`;
    }
  };

  useLayoutEffect(() => {
    if (inView && !mobileView && !disabled) {
      if (elemOffsetTop === -1) {
        setElemOffsetTop(scroll);
      }

      if (!hasInit) {
        initAnim();
      }
    }
  }, [inView, hasInit, mobileView, disabled]);

  useLayoutEffect(() => {
    setInit(!enableEnterAnim || mobileView);

    if (!mobileView) {
      animate();
    }
  }, [mobileView]);

  useEffect(() => {
    if (inView && !disabled && !mobileView && hasInit) {
      animate();
    }
  }, [scroll]);

  return (
    <InView onChange={inView => setInView(inView)}>
      <div ref={wrapperEl} style={{ willChange: 'transform' }} {...other}>
        {children}
      </div>
    </InView>
  );
};

export default ScrollMotion;
